// src/sw-register.js
// Registers the service worker and wires progress reporting to the splash UI.
// No-op (with a console.warn) if the browser doesn't support SW or if
// registration fails — the app falls back to the current "fetch every sample
// on first note" behavior.

const basePath = import.meta.env.BASE_URL;
const SW_URL = `${basePath}sw.js`;
const SW_SCOPE = basePath;
const EXPECTED_SAMPLE_COUNT = 157; // must match SAMPLE_URLS.length in public/sw.js
const CACHE_NAME = 'helix-samples-v1-samples'; // must match SAMPLE_CACHE in public/sw.js
const TOTAL_BYTES = 29_000_000; // approximate; for display only

/**
 * Drive the splash UI based on the current cache state.
 *   state:   'idle' | 'downloading' | 'ready' | 'error' | 'unsupported'
 *   detail:  { loaded, total, percent, mb, error }
 */
function setSplashState(state, detail = {}) {
  const splash = document.getElementById('start-screen');
  const bar = document.getElementById('cache-progress');
  const fill = document.getElementById('cache-progress-fill');
  const label = document.getElementById('cache-progress-label');
  const status = document.getElementById('cache-status');
  const btn = document.getElementById('start-button');
  if (!splash || !bar || !fill || !label || !status || !btn) return;

  const enableButton = () => {
    btn.disabled = false;
    btn.classList.remove('is-disabled');
  };
  const disableButton = () => {
    btn.disabled = true;
    btn.classList.add('is-disabled');
  };

  switch (state) {
    case 'idle':
      // Don't wait for the cache — samples stream on-demand.
      status.classList.remove('is-error');
      status.textContent = 'Preparing samples...';
      enableButton();
      window.__helixCacheReady = true;
      break;

    case 'downloading': {
      const loaded = detail.loaded || 0;
      const total = detail.total || EXPECTED_SAMPLE_COUNT;
      const pct = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
      const mb = detail.mb != null ? detail.mb : (loaded / total) * (TOTAL_BYTES / 1_000_000);
      bar.classList.remove('hidden');
      fill.style.width = pct + '%';
      label.textContent =
        `Downloading samples... ${mb.toFixed(1)} / ${(TOTAL_BYTES / 1_000_000).toFixed(0)} MB (${pct}%)`;
      enableButton();
      break;
    }

    case 'ready':
      bar.classList.add('hidden');
      fill.style.width = '100%';
      status.classList.remove('is-error');
      status.textContent = 'Samples ready. Click to start composing.';
      enableButton();
      window.__helixCacheReady = true;
      break;

    case 'error':
      bar.classList.add('hidden');
      status.classList.remove('is-error');
      status.textContent = '';
      // Samples still load on-demand from the network — no need to block.
      enableButton();
      window.__helixCacheReady = true;
      break;

    case 'unsupported':
      // Browser doesn't support SW (e.g. private mode, ad blocker, file://).
      // Don't gate the start button; fall back to today's behavior.
      bar.classList.add('hidden');
      status.classList.remove('is-error');
      status.textContent = '';
      enableButton();
      window.__helixCacheReady = true;
      // eslint-disable-next-line no-console
      console.warn('[helix-composer] Service workers not supported; samples will be fetched on demand.');
      break;
  }
}

/**
 * Probe the cache to see if all sample URLs are already there. Used on
 * returning visits when the SW was activated before the page loaded its
 * message listener (the SW's install would be a no-op in that case and
 * no `cache-complete` message would fire).
 */
async function probeCache() {
  try {
    const cache = await caches.open(CACHE_NAME);
    const keys = await cache.keys();
    return keys.length >= EXPECTED_SAMPLE_COUNT;
  } catch {
    return false;
  }
}

export function initSampleCache() {
  if (!('serviceWorker' in navigator)) {
    setSplashState('unsupported');
    return;
  }

  setSplashState('idle');

  // Listen for progress messages from the SW. Register BEFORE the
  // registration promise resolves so we don't miss the first message.
  navigator.serviceWorker.addEventListener('message', (event) => {
    const msg = event.data || {};
    if (msg.type === 'cache-start') {
      setSplashState('downloading', { loaded: 0, total: msg.total, percent: 0, mb: 0 });
    } else if (msg.type === 'cache-progress') {
      const mb = (msg.index / msg.total) * (TOTAL_BYTES / 1_000_000);
      setSplashState('downloading', {
        loaded: msg.index,
        total: msg.total,
        percent: (msg.index / msg.total) * 100,
        mb
      });
    } else if (msg.type === 'cache-complete') {
      setSplashState('ready');
    } else if (msg.type === 'cache-error') {
      setSplashState('error', { error: msg.error });
    }
  });

  navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE })
    .then(async (reg) => {
      // If a worker is already active (returning visitor) the install
      // would be a no-op and no progress messages would fire. Probe the
      // cache directly to know whether to flip to 'ready'.
      if (reg.active && reg.active.state === 'activated') {
        const full = await probeCache();
        if (full) setSplashState('ready');
        // If not full, the SW is presumably still installing — let its
        // progress messages drive the UI.
      }
      // If a new worker is installing/waiting, its postMessage stream
      // will arrive via the listener above.
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[helix-composer] SW registration failed; samples will be fetched on demand.', err);
      setSplashState('unsupported');
    });

  // Best-effort persistent storage. The Cache API works either way, but
  // persist() improves robustness against browser eviction under storage
  // pressure. Silently ignored if denied or unsupported (Safari).
  if (navigator.storage && navigator.storage.persist) {
    navigator.storage.persist().catch(() => { /* noop */ });
  }
}
